import { create } from 'zustand';
import type { AdjustmentId } from '../adjustments';
import { BUILTIN_PRESETS } from '../brush/presets';
import { applyPreset, type BrushPreset, type PaintOptions } from '../brush/types';
import type { MaskViewMode } from '../core/channels';
import { BLACK, WHITE } from '../core/color';
import { BUILTIN_GRADIENTS, type Gradient, type GradientSettings } from '../core/gradient';
import type { GridSettings, SnapSettings } from '../core/snap';
import type { SelectionModifyKind } from '../engine/selectionOps';
import {
  isPaintTool,
  type ChannelRef,
  type DocumentMeta,
  type PaintTargetKind,
  type PaintToolId,
  type RGBA,
  type ToolId,
  type ToolOptions,
  type ViewState,
} from '../core/types';
import { readStoredLocale, writeStoredLocale, type Locale } from '../i18n';

export type TransformTarget = 'layer' | 'selection';

export type PanelId =
  | 'layers'
  | 'history'
  | 'color'
  | 'navigator'
  | 'brush'
  | 'channels'
  | 'paths'
  | 'properties'
  | 'info'
  | 'histogram';

export type PanelTabStackId = 'upper' | 'lower';

export type PanelTabsState = {
  upper: 'history' | 'color';
  lower: 'layers' | 'channels' | 'paths';
};

/** Flyout opened from the right icon rail (Photopea-style). */
export type PanelFlyoutId =
  | 'info'
  | 'properties'
  | 'brush'
  | 'character'
  | 'paragraph'
  | 'histogram';

const UPPER_TAB_PANELS: ReadonlySet<PanelId> = new Set(['history', 'color']);
const LOWER_TAB_PANELS: ReadonlySet<PanelId> = new Set(['layers', 'channels', 'paths']);
const FLYOUT_BY_PANEL: Partial<Record<PanelId, PanelFlyoutId>> = {
  info: 'info',
  properties: 'properties',
  brush: 'brush',
  histogram: 'histogram',
};

function panelTabsFor(panel: PanelId, current: PanelTabsState): PanelTabsState {
  if (UPPER_TAB_PANELS.has(panel)) {
    return { ...current, upper: panel as PanelTabsState['upper'] };
  }
  if (LOWER_TAB_PANELS.has(panel)) {
    return { ...current, lower: panel as PanelTabsState['lower'] };
  }
  return current;
}

export type DialogState =
  | { kind: 'newDocument' }
  | { kind: 'imageSize' }
  | { kind: 'canvasSize' }
  | { kind: 'export' }
  | { kind: 'adjustment'; adjustment: AdjustmentId }
  | { kind: 'fill' }
  | { kind: 'stroke' }
  | { kind: 'gradient' }
  | { kind: 'selectionModify'; modify: SelectionModifyKind }
  | { kind: 'colorRange' }
  | { kind: 'refineEdge' }
  | { kind: 'vectorize' }
  | { kind: 'rotateView' }
  | { kind: 'newGuide' }
  | { kind: 'gridSettings' }
  | { kind: 'offset' }
  | { kind: 'filter'; filter: import('../filters').FilterId }
  | { kind: 'liquify' }
  | { kind: 'layerStyle' }
  | { kind: 'shortcuts' }
  | { kind: 'tutorial' }
  | { kind: 'language' };

export interface HistoryView {
  labels: string[];
  position: number;
  canUndo: boolean;
  canRedo: boolean;
  bytes: number;
}

export const DEFAULT_PAINT_OPTIONS: PaintOptions = {
  size: 40,
  hardness: 0.7,
  opacity: 1,
  flow: 1,
  spacing: 0.12,
  angle: 0,
  roundness: 1,
  tipImageId: null,
  blendMode: 'normal',
  smoothing: 0.35,
  presetId: null,
  sizeJitter: 0,
  angleJitter: 0,
  roundnessJitter: 0,
  scatter: 0,
  flowJitter: 0,
  followDirection: false,
  pressureSize: true,
  pressureOpacity: false,
  strength: 0.5,
  exposure: 0.3,
  range: 'midtones',
  sponge: 'saturate',
  aligned: true,
  sampleAllLayers: false,
};

/** Each paint tool starts from the defaults Photoshop ships for it. */
export const DEFAULT_PAINT_BY_TOOL: Record<PaintToolId, PaintOptions> = {
  brush: { ...DEFAULT_PAINT_OPTIONS },
  pencil: { ...DEFAULT_PAINT_OPTIONS, size: 6, hardness: 1, spacing: 0.05 },
  eraser: { ...DEFAULT_PAINT_OPTIONS, size: 60, hardness: 0.5 },
  clone: { ...DEFAULT_PAINT_OPTIONS, size: 60, hardness: 0.9, spacing: 0.05 },
  blur: { ...DEFAULT_PAINT_OPTIONS, size: 60, hardness: 0.3, spacing: 0.05 },
  sharpen: { ...DEFAULT_PAINT_OPTIONS, size: 40, hardness: 0.5, spacing: 0.05, strength: 0.35 },
  smudge: { ...DEFAULT_PAINT_OPTIONS, size: 40, hardness: 0.4, spacing: 0.04, strength: 0.5 },
  dodge: { ...DEFAULT_PAINT_OPTIONS, size: 80, hardness: 0, spacing: 0.05 },
  burn: { ...DEFAULT_PAINT_OPTIONS, size: 80, hardness: 0, spacing: 0.05 },
  sponge: { ...DEFAULT_PAINT_OPTIONS, size: 80, hardness: 0, spacing: 0.05, strength: 0.4 },
  spotHealing: { ...DEFAULT_PAINT_OPTIONS, size: 40, hardness: 0.9, spacing: 0.05 },
  healing: { ...DEFAULT_PAINT_OPTIONS, size: 40, hardness: 0.9, spacing: 0.05 },
};

export const DEFAULT_GRADIENT: GradientSettings = {
  value: BUILTIN_GRADIENTS[0],
  presets: BUILTIN_GRADIENTS,
  type: 'linear',
  opacity: 1,
  blendMode: 'normal',
  reverse: false,
  dither: true,
};

export const DEFAULT_TOOL_OPTIONS: ToolOptions = {
  bucketTolerance: 0.12,
  bucketContiguous: true,
  selectionMode: 'replace',
  selectionFeather: 0,
  selectionAntialias: true,
  wandTolerance: 0.12,
  wandContiguous: true,
  wandSampleAllLayers: false,
  quickSelectSize: 30,
  magneticWidth: 12,
  magneticContrast: 0.1,
  magneticFrequency: 20,
  transformKeepAspect: true,
  transformMode: 'free',
  transformAnchor: 'center',
  eyedropperFromComposite: true,
  vectorMode: 'shape',
  pathOp: 'add',
  shapeRadius: 0,
  shapeSides: 5,
  shapeStarRatio: 1,
  shapeFillEnabled: true,
  shapeStrokeEnabled: false,
  shapeStrokeWidth: 2,
  shapeStrokeAlign: 'center',
  penTolerance: 2,
  penRubberBand: true,
  textFontFamily: 'Arial',
  textFontSize: 48,
  textAlign: 'left',
  textBold: false,
  textItalic: false,
};

const EMPTY_HISTORY: HistoryView = {
  labels: [],
  position: -1,
  canUndo: false,
  canRedo: false,
  bytes: 0,
};

export interface AppState {
  documents: DocumentMeta[];
  activeDocumentId: string | null;
  views: Record<string, ViewState>;
  history: Record<string, HistoryView>;
  /**
   * Bumped whenever layer pixels change without a history step, e.g. when a
   * session is restored. Thumbnails watch it so they never show stale content.
   */
  pixelEpoch: number;

  tool: ToolId;
  toolOptions: ToolOptions;
  /** Brush settings per paint tool, so switching tools keeps each one's feel. */
  paintOptions: Record<PaintToolId, PaintOptions>;
  brushPresets: BrushPreset[];
  /** Whether painting lands on the layer or on its mask. */
  paintTarget: PaintTargetKind;
  gradient: GradientSettings;
  /** Tile the Fill dialog and the pattern stamp use; ids come from the library. */
  activePatternId: string | null;
  foreground: RGBA;
  background: RGBA;

  dialog: DialogState | null;
  locale: Locale;
  status: string;
  fatalError: string | null;
  /** True between `webglcontextlost` and a successful re-initialisation. */
  contextLost: boolean;
  panels: Record<PanelId, boolean>;
  /** Active tab inside each Photoshop-style panel stack. */
  panelTabs: PanelTabsState;
  /** Right-rail flyout panel; null means no flyout open. */
  panelFlyout: PanelFlyoutId | null;
  /** Tab leaves only the canvas on screen; Shift+Tab hides the side column. */
  chromeHidden: boolean;
  panelColumnHidden: boolean;
  /** Quick Mask turns the selection into a paintable red overlay. */
  quickMask: boolean;
  /** Row selected in the Channels panel; strokes land there. */
  activeChannel: ChannelRef;
  /** How the active layer's mask is shown on the canvas. */
  maskView: MaskViewMode;
  /** Whether Free Transform warps the layer or the selection outline. */
  transformTarget: TransformTarget;
  /** View menu toggles for the canvas furniture. */
  showRulers: boolean;
  showGuides: boolean;
  showGrid: boolean;
  guidesLocked: boolean;
  grid: GridSettings;
  snap: SnapSettings;

  setTool: (tool: ToolId) => void;
  setTransformTarget: (target: TransformTarget) => void;
  patchToolOptions: (patch: Partial<ToolOptions>) => void;
  patchPaintOptions: (tool: PaintToolId, patch: Partial<PaintOptions>) => void;
  applyBrushPreset: (tool: PaintToolId, preset: BrushPreset) => void;
  addBrushPresets: (presets: BrushPreset[]) => void;
  removeBrushPreset: (presetId: string) => void;
  setPaintTarget: (target: PaintTargetKind) => void;
  patchGradient: (patch: Partial<GradientSettings>) => void;
  addGradientPreset: (gradient: Gradient) => void;
  setActivePattern: (patternId: string | null) => void;
  setForeground: (color: RGBA) => void;
  setBackground: (color: RGBA) => void;
  swapColors: () => void;

  setDocuments: (documents: DocumentMeta[]) => void;
  addDocument: (document: DocumentMeta, view: ViewState) => void;
  replaceDocument: (docId: string, updater: (doc: DocumentMeta) => DocumentMeta) => void;
  setDocument: (doc: DocumentMeta) => void;
  removeDocument: (docId: string) => void;
  setActiveDocument: (docId: string | null) => void;

  setView: (docId: string, view: ViewState) => void;
  patchView: (docId: string, patch: Partial<ViewState>) => void;
  setHistoryView: (docId: string, view: HistoryView) => void;
  bumpPixelEpoch: () => void;

  openDialog: (dialog: DialogState) => void;
  closeDialog: () => void;
  setLocale: (locale: Locale) => void;
  setStatus: (status: string) => void;
  setFatalError: (message: string | null) => void;
  setContextLost: (value: boolean) => void;
  togglePanel: (panel: PanelId) => void;
  openPanel: (panel: PanelId) => void;
  setPanelTab: (stack: PanelTabStackId, panel: PanelTabsState[PanelTabStackId]) => void;
  setPanelFlyout: (flyout: PanelFlyoutId | null) => void;
  togglePanelFlyout: (flyout: PanelFlyoutId) => void;
  toggleChrome: () => void;
  togglePanelColumn: () => void;
  setQuickMask: (value: boolean) => void;
  setActiveChannel: (channel: ChannelRef) => void;
  setMaskView: (mode: MaskViewMode) => void;
  toggleRulers: () => void;
  toggleGuides: () => void;
  toggleGrid: () => void;
  toggleGuidesLocked: () => void;
  patchGrid: (patch: Partial<GridSettings>) => void;
  patchSnap: (patch: Partial<SnapSettings>) => void;
}

export const useAppStore = create<AppState>((set) => ({
  documents: [],
  activeDocumentId: null,
  views: {},
  history: {},
  pixelEpoch: 0,

  tool: 'brush',
  toolOptions: DEFAULT_TOOL_OPTIONS,
  paintOptions: Object.fromEntries(
    Object.entries(DEFAULT_PAINT_BY_TOOL).map(([tool, options]) => [tool, { ...options }]),
  ) as Record<PaintToolId, PaintOptions>,
  brushPresets: BUILTIN_PRESETS,
  paintTarget: 'layer',
  gradient: DEFAULT_GRADIENT,
  activePatternId: null,
  foreground: BLACK,
  background: WHITE,

  dialog: null,
  locale: readStoredLocale(),
  status: 'Ready',
  fatalError: null,
  contextLost: false,
  panels: {
    layers: true,
    history: true,
    color: true,
    navigator: false,
    brush: false,
    channels: false,
    paths: false,
    properties: true,
    info: false,
    histogram: false,
  },
  panelTabs: {
    upper: 'color',
    lower: 'layers',
  },
  panelFlyout: null,
  chromeHidden: false,
  panelColumnHidden: false,
  quickMask: false,
  activeChannel: { kind: 'composite' },
  maskView: 'off',
  transformTarget: 'layer',
  showRulers: false,
  showGuides: true,
  showGrid: false,
  guidesLocked: false,
  grid: { size: 64, subdivisions: 4 },
  snap: { enabled: true, toGuides: true, toGrid: true, toDocument: true },

  setTool: (tool) =>
    set((state) => ({
      tool,
      // Tip settings live in the Brush flyout; open it with every paint tool.
      ...(isPaintTool(tool)
        ? {
            panels: { ...state.panels, brush: true },
            panelFlyout: 'brush' as const,
            panelColumnHidden: false,
          }
        : {}),
    })),
  setTransformTarget: (transformTarget) => set({ transformTarget }),
  patchToolOptions: (patch) =>
    set((state) => ({ toolOptions: { ...state.toolOptions, ...patch } })),
  patchPaintOptions: (tool, patch) =>
    set((state) => ({
      paintOptions: { ...state.paintOptions, [tool]: { ...state.paintOptions[tool], ...patch } },
    })),
  applyBrushPreset: (tool, preset) =>
    set((state) => ({
      paintOptions: {
        ...state.paintOptions,
        [tool]: applyPreset(state.paintOptions[tool], preset),
      },
    })),
  addBrushPresets: (presets) =>
    set((state) => ({ brushPresets: [...state.brushPresets, ...presets] })),
  removeBrushPreset: (presetId) =>
    set((state) => ({
      brushPresets: state.brushPresets.filter((preset) => preset.id !== presetId),
    })),
  setPaintTarget: (paintTarget) => set({ paintTarget }),
  patchGradient: (patch) => set((state) => ({ gradient: { ...state.gradient, ...patch } })),
  addGradientPreset: (gradient) =>
    set((state) => ({
      gradient: { ...state.gradient, presets: [...state.gradient.presets, gradient] },
    })),
  setActivePattern: (activePatternId) => set({ activePatternId }),
  setForeground: (foreground) => set({ foreground }),
  setBackground: (background) => set({ background }),
  swapColors: () => set((state) => ({ foreground: state.background, background: state.foreground })),

  setDocuments: (documents) => set({ documents }),
  addDocument: (document, view) =>
    set((state) => ({
      documents: [...state.documents, document],
      activeDocumentId: document.id,
      views: { ...state.views, [document.id]: view },
      history: { ...state.history, [document.id]: EMPTY_HISTORY },
    })),
  replaceDocument: (docId, updater) =>
    set((state) => ({
      documents: state.documents.map((doc) => (doc.id === docId ? updater(doc) : doc)),
    })),
  setDocument: (doc) =>
    set((state) => ({
      documents: state.documents.map((existing) => (existing.id === doc.id ? doc : existing)),
    })),
  removeDocument: (docId) =>
    set((state) => {
      const documents = state.documents.filter((doc) => doc.id !== docId);
      const views = { ...state.views };
      const history = { ...state.history };
      delete views[docId];
      delete history[docId];
      const activeDocumentId =
        state.activeDocumentId === docId
          ? (documents[documents.length - 1]?.id ?? null)
          : state.activeDocumentId;
      return { documents, views, history, activeDocumentId };
    }),
  setActiveDocument: (activeDocumentId) => set({ activeDocumentId }),

  setView: (docId, view) => set((state) => ({ views: { ...state.views, [docId]: view } })),
  patchView: (docId, patch) =>
    set((state) => {
      const current = state.views[docId];
      if (!current) return {};
      return { views: { ...state.views, [docId]: { ...current, ...patch } } };
    }),
  setHistoryView: (docId, view) =>
    set((state) => ({ history: { ...state.history, [docId]: view } })),
  bumpPixelEpoch: () => set((state) => ({ pixelEpoch: state.pixelEpoch + 1 })),

  openDialog: (dialog) => set({ dialog }),
  closeDialog: () => set({ dialog: null }),
  setLocale: (locale) => {
    writeStoredLocale(locale);
    set({ locale });
  },
  setStatus: (status) => set({ status }),
  setFatalError: (fatalError) => set({ fatalError }),
  setContextLost: (contextLost) => set({ contextLost }),
  togglePanel: (panel) =>
    set((state) => {
      if (UPPER_TAB_PANELS.has(panel) || LOWER_TAB_PANELS.has(panel)) {
        return {
          panels: { ...state.panels, [panel]: true },
          panelTabs: panelTabsFor(panel, state.panelTabs),
          panelColumnHidden: false,
        };
      }
      const flyout = FLYOUT_BY_PANEL[panel];
      if (flyout) {
        const closing = state.panelFlyout === flyout;
        return {
          panels: { ...state.panels, [panel]: !closing },
          panelFlyout: closing ? null : flyout,
          panelColumnHidden: false,
        };
      }
      return { panels: { ...state.panels, [panel]: !state.panels[panel] } };
    }),
  openPanel: (panel) =>
    set((state) => ({
      panels: { ...state.panels, [panel]: true },
      panelTabs: panelTabsFor(panel, state.panelTabs),
      panelFlyout: FLYOUT_BY_PANEL[panel] ?? state.panelFlyout,
      panelColumnHidden: false,
    })),
  setPanelTab: (stack, panel) =>
    set((state) => ({
      panelTabs: { ...state.panelTabs, [stack]: panel },
      panels: { ...state.panels, [panel]: true },
    })),
  setPanelFlyout: (panelFlyout) =>
    set({ panelFlyout, panelColumnHidden: false }),
  togglePanelFlyout: (flyout) =>
    set((state) => ({
      panelFlyout: state.panelFlyout === flyout ? null : flyout,
      panelColumnHidden: false,
    })),
  toggleChrome: () => set((state) => ({ chromeHidden: !state.chromeHidden })),
  togglePanelColumn: () => set((state) => ({ panelColumnHidden: !state.panelColumnHidden })),
  setQuickMask: (quickMask) => set({ quickMask }),
  setActiveChannel: (activeChannel) => set({ activeChannel }),
  setMaskView: (maskView) => set({ maskView }),
  toggleRulers: () => set((state) => ({ showRulers: !state.showRulers })),
  toggleGuides: () => set((state) => ({ showGuides: !state.showGuides })),
  toggleGrid: () => set((state) => ({ showGrid: !state.showGrid })),
  toggleGuidesLocked: () => set((state) => ({ guidesLocked: !state.guidesLocked })),
  patchGrid: (patch) => set((state) => ({ grid: { ...state.grid, ...patch } })),
  patchSnap: (patch) => set((state) => ({ snap: { ...state.snap, ...patch } })),
}));

/** Paint options of a tool, falling back to the Brush for non-paint tools. */
export function getPaintOptions(state: AppState, tool: ToolId = state.tool): PaintOptions {
  return isPaintTool(tool) ? state.paintOptions[tool] : state.paintOptions.brush;
}

export function getActiveDocument(state: AppState): DocumentMeta | null {
  if (!state.activeDocumentId) return null;
  return state.documents.find((doc) => doc.id === state.activeDocumentId) ?? null;
}

export function getActiveView(state: AppState): ViewState | null {
  if (!state.activeDocumentId) return null;
  return state.views[state.activeDocumentId] ?? null;
}

export function getHistoryView(state: AppState, docId: string | null): HistoryView {
  if (!docId) return EMPTY_HISTORY;
  return state.history[docId] ?? EMPTY_HISTORY;
}
